import React, { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Input, Icon } from '../../components/ui';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../lib/theme';
import { useAuth } from '../../contexts/AuthContext';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';

interface LoginPageProps {
  dark: boolean;
  onRegister: () => void;
  /** Called when the API returns mfa_required=true and mfa_setup_pending=false */
  onMfaChallenge?: () => void;
  /** Called when the API returns mfa_setup_pending=true */
  onMfaSetupRequired?: () => void;
}

function AuthShell({ children, dark }: { children: React.ReactNode; dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', background: t.bg }}>
      {/* Brand panel */}
      <div style={{
        flex: 1, background: t.card, display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 48, position: 'relative', overflow: 'hidden', borderRight: `1px solid ${t.border}`,
      }}>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{
            position: 'absolute', borderRadius: '50%',
            border: `1px solid ${dark ? 'rgba(181,41,122,0.15)' : 'rgba(181,41,122,0.07)'}`,
            width: 200 + i * 140, height: 200 + i * 140,
            top: '50%', left: '50%', transform: 'translate(-50%,-50%)', pointerEvents: 'none',
          }}/>
        ))}
        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: 380 }}>
          <img
            src={dark ? '/assets/senihelp24-logo-white.png' : '/assets/senihelp24-logo-color.png'}
            alt="Senihelp24"
            style={{ height: 68, objectFit: 'contain', marginBottom: 40 }}
          />
          <h1 style={{ fontSize: 28, fontWeight: 700, color: t.heading, marginBottom: 14, lineHeight: 1.25 }}>
            {tr('auth.login.tagline_line1')}<br/>{tr('auth.login.tagline_line2')}
          </h1>
        </div>
      </div>
      {/* Form panel */}
      <div style={{
        width: 480, background: t.bg,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px 48px', overflowY: 'auto',
      }}>
        {children}
      </div>
    </div>
  );
}

export function LoginPage({ dark, onRegister, onMfaChallenge, onMfaSetupRequired }: LoginPageProps) {
  const tc = getThemeColors(dark);
  // Alias: use tc for theme colors, tr for translations
  const t = tc;
  const { t: tr } = useTranslation();
  const { login, loginDemo } = useAuth();
  const [email, setEmail] = useState('admin@senihelp24.de');
  const [pw, setPw] = useState('demo1234');
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!email || !pw) { setErr(tr('auth.login.error.fill_all_fields')); return; }
    setErr(''); setLoading(true);
    try {
      const result = await login(email, pw, remember);
      if (result === 'mfa_setup') {
        onMfaSetupRequired?.();
      } else if (result === 'mfa_challenge') {
        onMfaChallenge?.();
      }
      // result === 'ok' → AuthContext already set token, App.tsx re-renders
    } catch (e) {
      // Surface the real error instead of silently logging in with a fake
      // demo token (the old fallback made every subsequent /v1/admin call
      // 401 and the empty-state UI looked deceptively like static mock data).
      const ax = e as { response?: { status?: number; data?: { errors?: Array<{ message?: string }>; message?: string } }; message?: string };
      if (ax.response?.status === 401) {
        setErr(tr('auth.login.error.invalid_credentials'));
      } else if (ax.response?.data?.errors?.[0]?.message) {
        setErr(ax.response.data.errors[0].message);
      } else if (ax.response?.data?.message) {
        setErr(ax.response.data.message);
      } else {
        setErr(ax.message ?? tr('auth.login.error.generic'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell dark={dark}>
      <div style={{ width: '100%', maxWidth: 360 }}>
        <div style={{ marginBottom: 28 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: t.heading }}>{tr('auth.login.title')}</h2>
          <p style={{ fontSize: 13, color: t.muted, marginTop: 6 }}>{tr('auth.login.subtitle')}</p>
        </div>

        {err && (
          <div style={{
            background: dark ? '#2a1212' : '#fef3f2',
            border: `1px solid ${dark ? '#3a1a1a' : '#fecdca'}`,
            borderRadius: 8, padding: '9px 13px', marginBottom: 14,
            fontSize: 12, color: '#b42318',
          }}>
            {err}
          </div>
        )}

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label={tr('auth.login.field.email.label')}>
            <Input
              type="email" value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder={tr('auth.login.field.email.placeholder')} dark={dark}
            />
          </Field>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
              <label style={{ fontSize: 12, fontWeight: 500, color: t.text }}>{tr('auth.login.field.password.label')}</label>
              <a href="#" onClick={e => e.preventDefault()} style={{ fontSize: 12, color: BRAND, textDecoration: 'none', fontWeight: 500 }}>
                {tr('auth.login.field.password.forgot')}
              </a>
            </div>
            <div style={{ position: 'relative' }}>
              <Input
                type={showPw ? 'text' : 'password'}
                value={pw}
                onChange={e => setPw(e.target.value)}
                placeholder="••••••••"
                dark={dark}
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowPw(v => !v)}
                style={{
                  position: 'absolute', right: 10, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none', border: 'none', cursor: 'pointer', display: 'flex',
                }}
              >
                <Icon.Eye c={t.muted} open={showPw}/>
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, cursor: 'pointer' }} onClick={() => setRemember(v => !v)}>
            <div style={{
              width: 18, height: 18, borderRadius: 4,
              border: `2px solid ${remember ? BRAND : t.border}`,
              background: remember ? BRAND : 'transparent',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              {remember && <Icon.Check c="#fff"/>}
            </div>
            <span style={{ fontSize: 12, color: t.text, userSelect: 'none' }}>{tr('auth.login.field.rememberMe.label')}</span>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              background: loading ? '#d9459d' : BRAND, color: '#fff',
              border: 'none', borderRadius: 9, padding: '11px',
              fontSize: 14, fontWeight: 600, cursor: loading ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 6,
              fontFamily: 'Outfit, sans-serif',
            }}
          >
            {loading ? (
              <>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" style={{ animation: 'spin 0.8s linear infinite' }}>
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
                </svg>
                {tr('auth.login.action.submitting')}
              </>
            ) : tr('auth.login.action.submit')}
          </button>
        </form>

        <div style={{ marginTop: 28, paddingTop: 18, borderTop: `1px solid ${t.border}`, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
          {/* Language switcher in footer for unauthenticated users */}
          <LanguageSwitcher dark={dark} variant="footer" />
        </div>
      </div>
    </AuthShell>
  );
}

// ─── Register Page ────────────────────────────────────────────────────────

interface RegisterPageProps {
  dark: boolean;
  onLogin: () => void;
}

interface RegisterForm {
  invite: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  password: string;
  confirm: string;
  dsgvo: boolean;
}

const ROLES: { value: string; labelKey: string }[] = [
  { value: 'Koordinator/in',        labelKey: 'auth.register.role.coordinator' },
  { value: 'Pflegedienstleitung',   labelKey: 'auth.register.role.pdl' },
  { value: 'Vertrieb',              labelKey: 'auth.register.role.sales' },
  { value: 'Administration',        labelKey: 'auth.register.role.administration' },
  { value: 'IT / Support',          labelKey: 'auth.register.role.it_support' },
];

export function RegisterPage({ dark, onLogin }: RegisterPageProps) {
  const tc = getThemeColors(dark);
  const t = tc;
  const { t: tr } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState<RegisterForm>({
    invite: 'SH24-2026-XY', firstName: '', lastName: '', email: '',
    phone: '', role: '', password: '', confirm: '', dsgvo: false,
  });
  const [errs, setErrs] = useState<Partial<Record<keyof RegisterForm, string>>>({});

  const setField = (k: keyof RegisterForm) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  const v1 = () => {
    const e: typeof errs = {};
    if (!form.firstName) e.firstName = tr('common.field.required_error');
    if (!form.lastName) e.lastName = tr('common.field.required_error');
    if (!form.email || !form.email.includes('@')) e.email = tr('common.field.email.error');
    setErrs(e); return !Object.keys(e).length;
  };

  const v2 = () => {
    const e: typeof errs = {};
    if (!form.role) e.role = tr('common.field.role.error');
    if (form.password.length < 8) e.password = tr('common.field.password.error_min_length');
    if (form.password !== form.confirm) e.confirm = tr('common.field.password.error_mismatch');
    if (!form.dsgvo) e.dsgvo = tr('auth.register.consent.error');
    setErrs(e); return !Object.keys(e).length;
  };

  const pwStr = () => {
    const p = form.password; if (!p) return 0; let s = 0;
    if (p.length >= 8) s++; if (/[A-Z]/.test(p)) s++; if (/[0-9]/.test(p)) s++; if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  };
  const sl = ['', tr('common.field.password.strength.weak'), tr('common.field.password.strength.fair'), tr('common.field.password.strength.good'), tr('common.field.password.strength.strong')];
  const sc = ['', '#f04438', '#f79009', '#12b76a', '#039855'];
  const pw = pwStr();

  if (done) {
    return (
      <AuthShell dark={dark}>
        <div style={{ textAlign: 'center', maxWidth: 340 }}>
          <div style={{
            width: 60, height: 60, borderRadius: '50%', background: '#ecfdf3',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20,
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#027a48" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: t.heading, marginBottom: 10 }}>{tr('auth.register.success.title')}</h2>
          <p style={{ fontSize: 13, color: t.muted, marginBottom: 22, lineHeight: 1.6 }}>
            {tr('auth.register.success.message', { email: form.email })}
          </p>
          <button
            onClick={onLogin}
            style={{ background: BRAND, color: '#fff', border: 'none', borderRadius: 9, padding: '11px 28px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}
          >
            {tr('auth.register.action.to_login')}
          </button>
        </div>
      </AuthShell>
    );
  }

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!v2()) return;
    setLoading(true);
    setTimeout(() => { setLoading(false); setDone(true); }, 1100);
  };

  return (
    <AuthShell dark={dark}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: BRAND_LIGHT, color: BRAND,
            borderRadius: 9999, padding: '4px 10px', fontSize: 11, fontWeight: 600, marginBottom: 14,
          }}>
            <svg width="11" height="11" viewBox="0 0 20 20" fill={BRAND}><path d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm1 12H9v-5h2v5zm0-7H9V5h2v2z"/></svg>
            {tr('auth.register.invite_code', { code: form.invite })}
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: t.heading }}>
            {step === 1 ? tr('auth.register.title.step1') : tr('auth.register.title.step2')}
          </h2>
          <p style={{ fontSize: 12, color: t.muted, marginTop: 5 }}>
            {tr('auth.register.step_indicator', { step })}
          </p>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 22 }}>
          {[1, 2].map(i => (
            <div key={i} style={{
              flex: 1, height: 4, borderRadius: 3,
              background: step >= i ? BRAND : t.border,
              transition: 'background 0.2s',
            }}/>
          ))}
        </div>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {step === 1 ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label={tr('common.field.firstName.label')} error={errs.firstName}><Input value={form.firstName} onChange={setField('firstName')} placeholder={tr('common.field.firstName.placeholder')} dark={dark}/></Field>
                <Field label={tr('common.field.lastName.label')} error={errs.lastName}><Input value={form.lastName} onChange={setField('lastName')} placeholder={tr('common.field.lastName.placeholder')} dark={dark}/></Field>
              </div>
              <Field label={tr('common.field.email.label')} error={errs.email} hint={tr('common.field.email.hint')}>
                <Input type="email" value={form.email} onChange={setField('email')} placeholder="m.mueller@senihelp24.de" dark={dark}/>
              </Field>
              <Field label={tr('common.field.phone.label')}>
                <Input value={form.phone} onChange={setField('phone')} placeholder={tr('common.field.phone.placeholder')} dark={dark}/>
              </Field>
              <button
                type="button"
                onClick={() => { if (v1()) setStep(2); }}
                style={{ background: BRAND, color: '#fff', border: 'none', borderRadius: 9, padding: '11px', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 4, fontFamily: 'Outfit, sans-serif' }}
              >
                {tr('auth.register.action.next')}
              </button>
            </>
          ) : (
            <>
              <Field label={tr('common.field.role.label')} error={errs.role}>
                <select
                  value={form.role}
                  onChange={setField('role')}
                  style={{
                    width: '100%', border: `1px solid ${errs.role ? '#fda29b' : t.border}`,
                    borderRadius: 9, padding: '9px 12px', fontSize: 13,
                    color: form.role ? t.heading : t.placeholder,
                    background: t.inputBg, cursor: 'pointer', fontFamily: 'Outfit, sans-serif',
                  }}
                >
                  <option value="">{tr('common.field.role.placeholder')}</option>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{tr(r.labelKey)}</option>)}
                </select>
              </Field>
              <Field label={tr('common.field.password.label')} error={errs.password} hint={tr('common.field.password.hint')}>
                <Input type="password" value={form.password} onChange={setField('password')} placeholder="••••••••" dark={dark}/>
                {form.password && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 4, alignItems: 'center' }}>
                    {[1, 2, 3, 4].map(i => (
                      <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: pw >= i ? sc[pw] : t.border }}/>
                    ))}
                    <span style={{ fontSize: 10, color: sc[pw], fontWeight: 500, marginLeft: 6 }}>{sl[pw]}</span>
                  </div>
                )}
              </Field>
              <Field label={tr('common.field.confirm_password.label')} error={errs.confirm}>
                <Input type="password" value={form.confirm} onChange={setField('confirm')} placeholder="••••••••" dark={dark}/>
              </Field>
              <div
                onClick={() => setForm(f => ({ ...f, dsgvo: !f.dsgvo }))}
                style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer', marginTop: 4 }}
              >
                <div style={{
                  width: 18, height: 18, borderRadius: 4,
                  border: `2px solid ${errs.dsgvo ? '#fda29b' : form.dsgvo ? BRAND : t.border}`,
                  background: form.dsgvo ? BRAND : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1,
                }}>
                  {form.dsgvo && <Icon.Check c="#fff"/>}
                </div>
                <span style={{ fontSize: 12, color: t.text, lineHeight: 1.5, userSelect: 'none' }}>
                  {tr('auth.register.consent.label')}
                </span>
              </div>
              {errs.dsgvo && <p style={{ fontSize: 11, color: '#f04438', margin: 0 }}>{errs.dsgvo}</p>}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{ flex: 1, background: t.card, color: t.text, border: `1px solid ${t.border}`, borderRadius: 9, padding: '11px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}
                >
                  {tr('auth.register.action.back')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  style={{ flex: 2, background: loading ? '#d9459d' : BRAND, color: '#fff', border: 'none', borderRadius: 9, padding: '11px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'Outfit, sans-serif' }}
                >
                  {loading ? tr('auth.register.action.submitting') : tr('auth.register.action.submit')}
                </button>
              </div>
            </>
          )}
        </form>

        <div style={{ marginTop: 22, textAlign: 'center', fontSize: 13, color: t.muted }}>
          {tr('auth.register.already_registered')}{' '}
          <a href="#" onClick={e => { e.preventDefault(); onLogin(); }} style={{ color: BRAND, fontWeight: 600, textDecoration: 'none' }}>
            {tr('auth.register.login_link')}
          </a>
        </div>
      </div>
    </AuthShell>
  );
}
